import {Injectable} from '@angular/core';
import {ParentService} from "./ParentService";
import {catchError} from "rxjs/operators";
import {HttpErrorResponse} from "@angular/common/http";
import {
  AiConfigDto,
  AiConfigSuggestionDto,
  AiPromptPreviewDto,
  AiSettingDefDto,
  AiSettingsDto
} from "../dto/AiConfigDto";

/**
 * Configuración de la IA: tipos de examen, sus variables y el preview del prompt.
 *
 * Va aparte de AiService porque no tiene nada que ver con generar ni revisar: aquí no se gasta un
 * euro, se configura.
 */
@Injectable()
export class AiConfigService extends ParentService {

  public getConfigs() {
    return this.http.get<AiConfigDto[]>(this.shared.MAGNAMENT_URL + '/ai/getConfigs/' + this.shared.CORP_ID, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public getProfiles() {
    return this.http.get<AiConfigDto[]>(this.shared.MAGNAMENT_URL + '/ai/getProfiles/' + this.shared.CORP_ID, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Crea o actualiza. Devuelve ok = false si el JSON no se puede leer, que es el fallo típico. */
  public saveConfig(config: AiConfigDto) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/saveConfig/' + this.shared.CORP_ID, config, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** No borra un tipo de examen que tenga cursos colgando. */
  public deleteConfig(id: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/deleteConfig/' + id, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public getSettingCatalogue() {
    return this.http.get<AiSettingDefDto[]>(this.shared.MAGNAMENT_URL + '/ai/getSettingCatalogue/' + this.shared.CORP_ID, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public saveSettingDefinition(definition: any) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/saveSettingDefinition/' + this.shared.CORP_ID, definition, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public deleteSettingDefinition(id: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/deleteSettingDefinition/' + id, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public getSettingTypes() {
    return this.http.get<any[]>(this.shared.MAGNAMENT_URL + '/ai/getSettingTypes', this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public getPromptCodes() {
    return this.http.get<any[]>(this.shared.MAGNAMENT_URL + '/ai/getPromptCodes', this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** La configuración que se aplica de verdad a un curso, con la procedencia de cada clave. */
  public resolveConfig(courseId: number) {
    return this.http.get<AiSettingsDto>(this.shared.MAGNAMENT_URL + '/ai/resolveConfig/' + this.shared.CORP_ID + '/' + (courseId == null ? 0 : courseId), this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** El prompt final montado, tal cual lo recibe el modelo. */
  public previewPrompt(courseId: number, code: string) {
    return this.http.get<AiPromptPreviewDto>(this.shared.MAGNAMENT_URL + '/ai/previewPrompt/' + this.shared.CORP_ID + '/' + (courseId == null ? 0 : courseId) + '/' + code, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Propone configuración mirando las preguntas que el curso ya tiene. No guarda nada. */
  public suggestConfig(courseId: number) {
    return this.http.get<AiConfigSuggestionDto>(this.shared.MAGNAMENT_URL + '/ai/suggestConfig/' + this.shared.CORP_ID + '/' + (courseId == null ? 0 : courseId), this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  // ------------------------------------------------------- imágenes de las preguntas de IA
  //
  // Cuatro salidas para la misma pregunta, porque una imagen generada no se puede comprobar por
  // código: si la anatomía está inventada, lo único que lo caza es que alguien la mire.

  /** Genera una imagen nueva desde cero. Las instrucciones son opcionales. */
  public generateQuestionImage(questionId: number, instructions: string) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/generateQuestionImage/' + this.shared.CORP_ID + '/' + questionId, {instructions: instructions}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Retoca la imagen actual partiendo de ella. La anterior se conserva. */
  public editQuestionImage(questionId: number, multimediaId: number, instructions: string) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/editQuestionImage/' + this.shared.CORP_ID + '/' + questionId + '/' + multimediaId, {instructions: instructions}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /**
   * Sube una imagen propia. shared.headers() no pone Content-Type, que es justo lo que hace falta:
   * con FormData lo pone el navegador con su boundary. Es el mismo patrón del resto de subidas.
   */
  public uploadQuestionImage(questionId: number, file: File) {
    const datos = new FormData();
    datos.append('file', file);
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/uploadQuestionImage/' + this.shared.CORP_ID + '/' + questionId, datos, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  public deleteQuestionImage(questionId: number, multimediaId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/deleteQuestionImage/' + questionId + '/' + multimediaId, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Recupera una versión anterior: la vuelve a colgar como última, sin borrar las posteriores. */
  public useQuestionImage(questionId: number, multimediaId: number) {
    return this.http.post<any>(this.shared.MAGNAMENT_URL + '/ai/useQuestionImage/' + this.shared.CORP_ID + '/' + questionId + '/' + multimediaId, {}, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }

  /** Todas las versiones de la imagen, para poder volver a una anterior. */
  public getQuestionImages(questionId: number) {
    return this.http.get<any[]>(this.shared.MAGNAMENT_URL + '/ai/getQuestionImages/' + questionId, this.shared.headers()).pipe(catchError((error: HttpErrorResponse) => this.shared.handleError(error)));
  }
}
